import { apiFetch } from "./client";

export type InsightsPeriod = "24h" | "48h" | "7d" | "14d" | "30d" | "90d";
export const INSIGHTS_PERIODS: InsightsPeriod[] = ["24h", "48h", "7d", "14d", "30d", "90d"];

export type CompareMode = "previous" | "none" | "custom";

/**
 * Either a preset or a hand-picked window (`since`/`until`, YYYY-MM-DD),
 * plus the baseline to measure it against. Every field is optional; the
 * server defaults to 30d against the previous 30d.
 */
export interface InsightsRangeQuery {
  period?: InsightsPeriod;
  since?: string;
  until?: string;
  compare?: CompareMode;
  compareSince?: string;
  compareUntil?: string;
}

function rangeParams(range: InsightsRangeQuery, extra: Record<string, string> = {}): URLSearchParams {
  const qs = new URLSearchParams(extra);
  for (const [k, v] of Object.entries(range)) if (v) qs.set(k, String(v));
  return qs;
}

/** The baseline window's own figures, and the deltas against it. */
export interface InsightsComparison {
  mode: CompareMode;
  since: string;
  until: string;
  days: number;
  runs: number;
  successRate: number | null;
  avgDurationMs: number | null;
  tokensIn: number;
  tokensOut: number;
  costCents: number;
  priced: boolean;
  costPerRunCents: number | null;
  runsChangePct: number | null;
  costChangePct: number | null;
  costPerRunChangePct: number | null;
  avgDurationChangePct: number | null;
  tokensPerRunChangePct: number | null;
  successRateChangePts: number | null;
}

export type FailureKind =
  | "tool_timeout"
  | "tool_error"
  | "await_timeout"
  | "agent_error"
  | "validation"
  | "cancelled"
  | "other";

export interface InsightsTotals {
  runs: number;
  completed: number;
  failed: number;
  cancelled: number;
  live: number;
  durationMsTotal: number;
  avgDurationMs: number | null;
  successRate: number | null;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  toolCalls: number;
  costCents: number;
  priced: boolean;
  turns: number;
  priorRuns: number;
  runsChangePct: number | null;
  costPerRunCents: number | null;
  comparison: InsightsComparison | null;
}

export interface TriggerSummary {
  subtype: string;
  label: string | null;
  cronExpression: string | null;
  timezone: string | null;
  channelId: string | null;
  channelName: string | null;
}

export interface FailureReason {
  kind: FailureKind;
  label: string;
  count: number;
}

export interface DayPoint {
  date: string;
  completed: number;
  failed: number;
}

export interface WorkflowInsightRow {
  id: string;
  name: string;
  status: string;
  trigger: TriggerSummary;
  agentIds: string[];
  runs: number;
  completed: number;
  failed: number;
  successRate: number | null;
  avgDurationMs: number | null;
  tokensIn: number;
  tokensOut: number;
  costCents: number;
  priced: boolean;
  lastRunAt: string | null;
}

export interface CompanyInsights {
  period: InsightsPeriod | null;
  days: number;
  since: string;
  until: string;
  agentId: string | null;
  totals: InsightsTotals;
  failureReasons: FailureReason[];
  perDay: DayPoint[];
  workflows: WorkflowInsightRow[];
}

export interface ChainNode {
  id: string;
  type: "trigger" | "agent" | "condition" | "action" | "decision";
  data: Record<string, unknown>;
}

export interface WorkflowInsights {
  workflow: {
    id: string;
    name: string;
    status: string;
    trigger: TriggerSummary;
    agentIds: string[];
    chain: ChainNode[];
  };
  period: InsightsPeriod | null;
  days: number;
  since: string;
  until: string;
  totals: InsightsTotals;
  failureReasons: FailureReason[];
  perDay: DayPoint[];
}

export interface InsightRunRow {
  id: string;
  status: string;
  triggerSource: string | null;
  currentStepId: string | null;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  tokensIn: number;
  tokensOut: number;
  /** Cache reads/writes, which on this fleet dwarf fresh input. */
  cacheReadTokens: number;
  cacheWriteTokens: number;
  toolCalls: number;
  costCents: number;
  /** False when nothing in the run had a catalogue price; 0 is not "free". */
  priced: boolean;
  /** Models the run actually used, heaviest first. */
  models: string[];
  failureKind: FailureKind | null;
  errorMessage: string | null;
  sessionId: string | null;
}

export interface InsightRunsPage {
  runs: InsightRunRow[];
  hasMore: boolean;
  failedCount: number;
}

export interface InsightStep {
  stepId: string;
  nodeType: "trigger" | "agent" | "condition" | "action" | string;
  label: string;
  agentId: string | null;
  status: "completed" | "failed" | "running" | "awaiting" | "skipped" | "pending";
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  attempt: number;
  branchTaken: string | null;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  toolCalls: number;
  error: { message: string; detail: string | null } | null;
  payloadPreview: string | null;
  outputPreview: string | null;
}

export interface RunInsights {
  run: {
    id: string;
    workflowId: string;
    status: string;
    triggerSource: string | null;
    startedAt: string;
    completedAt: string | null;
    durationMs: number | null;
    currentStepId: string | null;
    failedStepIndex: number | null;
    failureKind: FailureKind | null;
    errorMessage: string | null;
    sessionId: string | null;
    assignedAgent: { id: string; name: string } | null;
  };
  workflow: { id: string; name: string } | null;
  steps: InsightStep[];
  totals: {
    tokensIn: number;
    tokensOut: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    toolCalls: number;
    turns: number;
    costCents: number;
    priced: boolean;
  };
  similarFailures: number;
}

export const workflowInsightsApi = {
  company: (companyId: string, range: InsightsRangeQuery, agentId?: string | null) => {
    const qs = rangeParams(range, agentId ? { agentId } : {});
    return apiFetch<CompanyInsights>(`/companies/${companyId}/workflows/insights?${qs}`);
  },
  workflow: (workflowId: string, range: InsightsRangeQuery) =>
    apiFetch<WorkflowInsights>(`/workflows/${workflowId}/insights?${rangeParams(range)}`),
  runs: (
    workflowId: string,
    opts: InsightsRangeQuery & { status?: "failed" | "completed" | null; limit?: number; offset?: number },
  ) => {
    const { status, limit, offset, ...range } = opts;
    const qs = rangeParams(range, { limit: String(limit ?? 50) });
    if (status) qs.set("status", status);
    if (offset) qs.set("offset", String(offset));
    return apiFetch<InsightRunsPage>(`/workflows/${workflowId}/insights/runs?${qs}`);
  },
  run: (runId: string) => apiFetch<RunInsights>(`/workflow-runs/${runId}/insights`),
};
